import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import PortalDropdown, { DropdownMenuItem } from "views_shared/app/javascript/components/PortalDropdown";
import SectionCard from "../SectionCard";
import SectionSkeleton from "../SectionSkeleton";
import ServerErrorAlert from "../ServerErrorAlert";
import PaginationGroup from "../PaginationGroup";
import ConfirmModal from "../../confirm_modal";
import TabBar from "./TabBar";
import RepresentRow from "./RepresentRow";
import RepresentCard from "./RepresentCard";
import useRepresentsSection from "./use_represents_section";

/**
 * RepresentsSection — the shared tabbed "Represents" section card (chrome; the data + adaptation live
 * in the engine that builds `tabs`). A `<SectionCard>` wrapping a tab bar, a client-side search box,
 * a stacked list of <RepresentRow>s (one per `<li>`, divider between rows, none after the last), and a
 * client-side pager. Model-agnostic: each tab carries its own display rows and its own remove handler,
 * so the section knows nothing about representations.
 *
 * Adding is page-level, not tab-scoped: `addOptions` renders one "Add" dropdown (its items are the
 * entity types the engine can add) regardless of which tab is active, gated on `editable`. A row's
 * menu, by contrast, stays tab-scoped and renders only when `editable && onRemove`.
 *
 * Removal is confirmed here rather than in each engine: the row menu's item opens a ConfirmModal and
 * the tab's `onRemove` only runs once the viewer says yes. It must resolve whether the row went away —
 * `onRemove(row) => Promise<boolean>` (true = removed) — because the confirmation closes only on
 * success, leaving a failed removal on screen with its error. That error is the caller's
 * `generalError`, rendered in the card body, or inside the confirmation while one is up.
 *
 * While loading it renders a section-shaped skeleton; with no tabs it renders nothing.
 *
 * @param {Object} props
 * @param {Array} props.tabs - TabDescriptors `{ key, label, rows, onRemove?, emptyText?, layout?,
 *   perPage?, onShow? }`, where `onRemove(row) => Promise<boolean>` resolves true when the row was
 *   removed, `layout: "cards"` draws <RepresentCard>s two to a row, and `onShow` runs when the tab
 *   is first shown
 * @param {boolean} [props.editable] - Whether the viewer may add/remove (default false)
 * @param {Array} [props.addOptions] - `{ label, icon, onAdd }[]` rendered as the "Add" dropdown's
 *   menu items; omitted/empty hides the dropdown entirely (still gated on `editable`)
 * @param {string|null} [props.activeTabRequest] - A tab key to switch to once (e.g. after a
 *   successful add); consumed and cleared via `onActiveTabRequestHandled`
 * @param {Function} [props.onActiveTabRequestHandled] - Called once `activeTabRequest` has been
 *   applied (or found not to match any tab), so the caller can clear it
 * @param {boolean} [props.isLoading] - True while the tab data is loading (renders a skeleton)
 * @param {string} [props.title] - Section title (default "Represents")
 * @param {string} [props.id] - Optional DOM id (e.g. for system tests / deep links)
 * @param {number} [props.perPage] - Rows per page (default 10, as in Figma)
 * @param {string} [props.defaultTabKey] - Initially active tab key (default: first tab)
 * @param {string} [props.searchPlaceholder] - Search input placeholder (default "Search")
 * @param {*} [props.generalError] - The caller's stored save error, surfaced above the rows
 * @param {Function} [props.clearGeneralError] - Clears that error when a confirmation opens
 */
function RepresentsSection({
  tabs,
  editable = false,
  addOptions = undefined,
  activeTabRequest = null,
  onActiveTabRequestHandled = undefined,
  isLoading = false,
  title = "Represents",
  id = undefined,
  perPage = 10,
  defaultTabKey = undefined,
  searchPlaceholder = "Search",
  generalError = null,
  clearGeneralError = undefined,
}) {
  // A visitor only sees tabs with something in them; an editor keeps every tab to add into.
  const shownTabs = editable ? tabs : tabs.filter((tab) => (tab.rows || []).length > 0);

  const {
    activeTabKey,
    setActiveTabKey,
    query,
    setQuery,
    page,
    setPage,
    activeTab,
    visibleRows,
    pageCount,
    filteredCount,
    perPage: tabPerPage,
  } = useRepresentsSection({ tabs: shownTabs, perPage, defaultTabKey, activeTabRequest, onActiveTabRequestHandled });

  const onShowActiveTab = activeTab?.onShow;
  useEffect(() => {
    onShowActiveTab?.();
  }, [onShowActiveTab]);

  // The pending removal is held as `{ tabKey, row }`, never the tab's own onRemove: the engines rebuild
  // their tab descriptors on every render (each remover closes over the current collections and the
  // in-flight guard), so a remover captured when the confirmation opened would be stale by the time the
  // viewer confirms.
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  // Click-to-click re-entry guard. `removing` reaches the handler only after a re-render, so two clicks
  // in the same tick would both pass a state-based check and fire the removal twice.
  const removalInFlight = useRef(false);

  const confirmRemoval = async () => {
    if (!pendingRemoval || removalInFlight.current) return;

    const request = pendingRemoval;
    // Resolve the tab by key against the CURRENT tabs so the removal runs the remover this render owns.
    const tab = tabs.find((candidate) => candidate.key === request.tabKey);
    const stillListed = (tab?.rows || []).some((row) => row.id === request.row.id);
    // A reload or a reconcile can retire the row while the confirmation sits open. Nothing is left to
    // remove, so dismiss rather than destroy whatever took its place; any error stays in the card body.
    if (!tab || typeof tab.onRemove !== "function" || !stillListed) {
      setPendingRemoval(null);
      return;
    }

    removalInFlight.current = true;
    setRemoving(true);
    try {
      const removed = await tab.onRemove(request.row);
      // Close only the confirmation this removal started from — the viewer may have moved it onto
      // another row while the destroy was in flight.
      if (removed) setPendingRemoval((current) => (current === request ? null : current));
    } finally {
      removalInFlight.current = false;
      setRemoving(false);
    }
  };

  if (isLoading) return <SectionSkeleton lines={6} />;
  if (!shownTabs.length) return null;

  // toggleClassName reproduces the old bare button's exact visual recipe — no new visual language
  // invented for a page-level control replacing a tab-level one. No toggleAriaLabel: the visible
  // "Add" text is the toggle's accessible name, matching how the old bare button had none either.
  const addMenu =
    editable && addOptions?.length ? (
      <PortalDropdown toggleClassName="btn btn-primary btn-sm" toggleLabel="Add" toggleIcon="fa-solid fa-chevron-down">
        {addOptions.map((option) => (
          <DropdownMenuItem key={option.label} icon={option.icon} label={option.label} onClick={option.onAdd} />
        ))}
      </PortalDropdown>
    ) : null;

  // The row menu's item only opens the confirmation; the tab's onRemove runs from confirmRemoval.
  const rowRemover =
    editable && typeof activeTab?.onRemove === "function"
      ? (row) => {
          clearGeneralError?.();
          setPendingRemoval({ tabKey: activeTab.key, row });
        }
      : null;

  // The search only earns its place once the list is long enough to page — a card holding a handful of
  // rows (or none) reads as broken when it carries a filter above them. Gated on the tab's own rows,
  // not the filtered count, so typing a narrow query can never remove the box that produced it.
  const showSearch = (activeTab?.rows?.length || 0) > tabPerPage;
  const cards = activeTab?.layout === "cards";

  const errorAlert = <ServerErrorAlert generalError={generalError} />;

  return (
    <SectionCard id={id} title={title} headerActions={addMenu}>
      {/* While a confirmation is up it carries the alert itself — a failed removal has to be readable
          without dismissing the modal that reports it. */}
      {!pendingRemoval && errorAlert}

      <TabBar tabs={shownTabs} activeTabKey={activeTabKey} onSelect={setActiveTabKey} />

      {showSearch && (
        <div className="represents-search mt-4">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            type="search"
            className="form-control"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      )}

      {filteredCount === 0 ? (
        <div className="text-muted mt-3">
          {query
            ? `No ${typeof activeTab?.label === "string" ? activeTab.label.toLowerCase() : "results"} match “${query}”.`
            : activeTab?.emptyText || "Nothing here yet."}
        </div>
      ) : (
        <>
          <div className="border border-accent-blue-10 rounded-3 px-4 py-3 mt-4">
            {cards ? (
              <div className="represent-cards py-1">
                {visibleRows.map((row) => (
                  <RepresentCard key={row.id} row={row} onRemove={rowRemover} />
                ))}
              </div>
            ) : (
              <ul className="list-unstyled mb-0">
                {visibleRows.map((row, index) => (
                  <li key={row.id} className={`py-2${index < visibleRows.length - 1 ? " border-bottom" : ""}`}>
                    <RepresentRow row={row} onRemove={rowRemover} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {filteredCount > tabPerPage && (
            <PaginationGroup
              className="mt-4"
              page={page}
              pageCount={pageCount}
              onPageChange={setPage}
              ariaLabel={`${title} pages`}
            />
          )}
        </>
      )}

      {pendingRemoval && (
        <ConfirmModal
          confirmYesText="Remove"
          confirmNoText="Cancel"
          confirmContent={
            <>
              <div>{`Stop representing ${pendingRemoval.row.title}?`}</div>
              {errorAlert}
            </>
          }
          confirmYes={confirmRemoval}
          // Cancel goes away while the removal is in flight: ConfirmModal swaps only the Yes button for
          // its spinner, so a live Cancel would dismiss a confirmation whose destroy is still running.
          confirmNo={removing ? undefined : () => setPendingRemoval(null)}
          isOpening
          className="modal-sm p-3"
          confirmNoButtonClassName="shadow-none"
          isSubmitting={removing}
        />
      )}
    </SectionCard>
  );
}

RepresentsSection.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      rows: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }))
        .isRequired,
      onRemove: PropTypes.func,
      emptyText: PropTypes.string,
      layout: PropTypes.oneOf(["list", "cards"]),
      perPage: PropTypes.number,
      onShow: PropTypes.func,
    }),
  ).isRequired,
  editable: PropTypes.bool,
  addOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      onAdd: PropTypes.func.isRequired,
    }),
  ),
  activeTabRequest: PropTypes.string,
  onActiveTabRequestHandled: PropTypes.func,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  id: PropTypes.string,
  perPage: PropTypes.number,
  defaultTabKey: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  generalError: PropTypes.any,
  clearGeneralError: PropTypes.func,
};

export default RepresentsSection;
